"use client";

import * as React from "react";
import { cn } from "../../lib/utils";


export interface ProfileStat {
  label: string;
  value: string;
  color?: string;
  icon?: React.ReactNode;
  url?: string;
  provider?: string;
}

export interface ProfileCardProps {
  name: string;
  subtitle: string;
  avatar?: string;
  backgroundImage?: string;
  backgroundColor?: string;
  titleColor?: string;
  textColor?: string;
  stats?: ProfileStat[];
  showStatLabels?: boolean;
  className?: string;
  onStatClick?: (stat: ProfileStat) => void;
  nameTag?: React.ElementType;
}

const getAccessibleStatLabel = (stat: ProfileStat) => {
  const trimmedLabel = stat.label?.trim();
  if (trimmedLabel) return trimmedLabel;

  const trimmedProvider = stat.provider?.trim();
  if (trimmedProvider) {
    return trimmedProvider.charAt(0).toUpperCase() + trimmedProvider.slice(1);
  }

  return "Social profile";
};

const getInitials = (value: string) => {
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? words[words.length - 1]?.[0] ?? "" : "";
  return `${first}${last}`.toUpperCase();
};

function BlueProfileDecoration({ className }: { className?: string }) {
  return (
    <svg width="68" height="65" viewBox="0 0 68 65" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M10.1765 0.929187C-1.71389 22.7546 -3.47155 37.6914 10.4115 63.5997L66.6141 29.3127C47.6341 -4.37971 10.1765 0.929187 10.1765 0.929187Z" fill="#1877F2" stroke="#101828" strokeWidth="1.24486" />
    </svg>

  );
}

function PinkProfileDecoration({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="38"
      viewBox="0 0 24 38"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4.03018 0.311523C6.03907 0.311523 7.68425 1.80037 7.89542 3.70215L7.98331 4.48535L8.45401 3.85352C9.61022 2.3054 11.7998 1.82465 13.538 2.80859C15.2735 3.79105 15.9393 5.88304 15.1523 7.63379L14.8232 8.36523L15.5595 8.04688C17.3557 7.27056 19.4948 7.93124 20.495 9.62988C21.4935 11.3256 21.009 13.4636 19.4296 14.5977L18.7665 15.0732L19.578 15.1602C21.53 15.3686 23.0409 16.988 23.0409 18.9443C23.0408 20.9006 21.5299 22.5191 19.578 22.7275L18.7665 22.8145L19.4296 23.29C21.0091 24.424 21.4933 26.562 20.495 28.2578C19.4948 29.9565 17.3558 30.6181 15.5595 29.8418L14.8232 29.5234L15.1523 30.2549C15.9392 32.0054 15.2734 34.0966 13.538 35.0791C11.7998 36.0631 9.61022 35.5823 8.45401 34.0342L7.98331 33.4023L7.89542 34.1865C7.68403 36.0881 6.03891 37.5771 4.03018 37.5771C2.02151 37.5771 0.37631 36.088 0.164948 34.1865L0.0780334 33.4023L-0.393646 34.0342C-1.54979 35.5823 -3.73844 36.0629 -5.47665 35.0791C-7.21218 34.0967 -7.87884 32.0056 -7.09189 30.2549L-6.76279 29.5234L-7.49911 29.8418C-9.2953 30.618 -11.4345 29.9564 -12.4347 28.2578C-13.4328 26.5621 -12.9485 24.424 -11.3692 23.29L-10.7061 22.8145L-11.5177 22.7275C-13.4694 22.519 -14.9795 20.9005 -14.9796 18.9443C-14.9796 16.9881 -13.4695 15.3687 -11.5177 15.1602L-10.7061 15.0732L-11.3692 14.5977C-12.9484 13.4635 -13.4331 11.3255 -12.4347 9.62988C-11.4344 7.93123 -9.29545 7.27059 -7.49911 8.04688L-6.76279 8.36523L-7.09189 7.63379C-7.87896 5.88302 -7.21224 3.79104 -5.47665 2.80859C-3.73855 1.82501 -1.54979 2.30565 -0.393646 3.85352L0.0780334 4.48535L0.164948 3.70215C0.376109 1.80042 2.02136 0.311598 4.03018 0.311523Z"
        fill="#FE91E8"
        stroke="#101828"
        strokeWidth="0.622422"
      />
    </svg>
  );
}

function SparkleDecoration({ className }: { className?: string }) {
  return (
    <svg
      width="22"
      height="37"
      viewBox="0 0 22 37"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M21.8316 18.0384C13.3936 18.0384 10.9158 22.133 10.9158 36.0767C10.9158 22.133 8.43795 18.0384 0 18.0384C8.43805 18.0384 10.9159 13.9438 10.9159 0C10.9158 13.9438 13.3937 18.0384 21.8316 18.0384Z"
        fill="#081219"
      />
    </svg>
  );
}

export function ProfileCard({
  name,
  subtitle,
  avatar,
  backgroundImage,
  backgroundColor = "#BEF0FA",
  titleColor = "#101828",
  textColor = "#344054",
  stats = [],
  showStatLabels = false,
  className,
  onStatClick,
  nameTag = "p",
}: ProfileCardProps) {
  const [avatarFailed, setAvatarFailed] = React.useState(false);
  const showAvatar = Boolean(avatar) && !avatarFailed;
  const initials = getInitials(name);
  const NameTag = nameTag;

  return (
    <div
      className={cn(
        "relative overflow-visible rounded-[16px] border border-black shadow-[0px_4px_0px_0px_#000000]",
        className
      )}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[16px]">
        {backgroundImage ? (
          <img
            src={backgroundImage}
            alt=""
            className="absolute inset-0 h-full w-full rounded-[16px] object-cover"
            aria-hidden="true"
          />
        ) : null}
        <div
          className="absolute inset-0 rounded-[16px]"
          style={{ backgroundColor }}
          aria-hidden="true"
        />
        <BlueProfileDecoration className="absolute -rotate-45 -right-8 top-[-1px] h-[85px] w-[73px]" />
        <SparkleDecoration className="absolute top-[-15px] left-[15px] h-[87px] w-[62px]" />
        <SparkleDecoration className="absolute bottom-[-15px] right-[15px] h-[47px] w-[32px]" />
        <PinkProfileDecoration className="absolute -rotate-45 bottom-[-35px] left-[15px] h-[69px] w-[69px]" />
      </div>

      <div className="relative z-10 flex flex-col items-center gap-1 px-4 pt-12 pb-3 text-center">
        <div className="absolute top-[-36px] h-20 w-20 rounded-full z-20 flex items-center justify-center">
          <div className="h-full w-full rounded-full border-2 overflow-hidden">
            {showAvatar ? (
              <img
                src={avatar}
                alt={name}
                className="h-full w-full object-cover"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <div className="h-full w-full bg-white flex items-center justify-center">
                <span className="text-[22px] font-black font-phudu text-[#101828]">
                  {initials || "?"}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <NameTag className="text-[24px] font-phudu font-black uppercase tracking-wide leading-tight z-20" style={{ color: titleColor }}>
            {name}
          </NameTag>
          <p className="text-xs font-medium leading-5 mx-auto" style={{ color: textColor }}>
            {subtitle}
          </p>
        </div>
        {stats.length > 0 && (
          <div
            className={cn(
              "flex flex-wrap items-center justify-center pt-1",
              showStatLabels ? "gap-3" : ""
            )}
          >
            {stats.map((stat, index) => {
              if (stat.provider && !stat.url) return null;
              const accessibleLabel = getAccessibleStatLabel(stat);

              const iconContent = stat.icon ?? (
                <span className="flex h-6 w-6 items-center justify-center rounded-full border border-[#101828] text-[10px] font-bold uppercase tracking-widest text-[#101828]">
                  {stat.label?.charAt(0) ?? "?"}
                </span>
              );
              const content = (
                <span className={cn("flex items-center justify-center", showStatLabels ? "flex-col gap-1" : "")}>
                  <span
                    className="flex h-6 w-6 items-center justify-center"
                    aria-hidden={!stat.label}
                  >
                    {iconContent}
                  </span>
                  {showStatLabels && stat.label ? (
                    <span
                      className="text-[10px] font-medium uppercase tracking-wide"
                      style={{ color: textColor }}
                    >
                      {stat.label}
                    </span>
                  ) : null}
                </span>
              );
              if (stat.url) {
                return (
                  <a
                    key={`${stat.label}-${index}`}
                    href={stat.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={accessibleLabel}
                    className="inline-flex cursor-pointer"
                    title={accessibleLabel}
                    onClick={() => onStatClick?.(stat)}
                  >
                    {content}
                  </a>
                );
              }
              return (
                <span key={`${stat.label}-${index}`} className="inline-flex">
                  {content}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
